import { useState } from "react";
import { api } from "../api";
import { Btn, Card, ConfirmDialog, Field, Input, Th, cellClip, formatDay,
         useSortableColumns, useColumnWidths, G_BG } from "../components/Shared";
import { useDisplayTimezone } from "../portalConfig";
import { useDocumentWord } from "../documentWord";

const td = { padding: "12px 16px", verticalAlign: "middle", fontSize: 14,
             color: "#374151", ...cellClip };

function GroupForm({ onSave, onCancel }) {
  const word = useDocumentWord();
  const [name, setName] = useState("");
  // Asked for here because the server refuses a group without it: a group
  // with no prefix of its own numbered from the shared "Q" series.
  const [prefix, setPrefix] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.post("/api/admin/groups", { name, quote_number_prefix: prefix.trim() });
      onSave(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card style={{ marginBottom: 24 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700 }}>New Group</h3>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <Field label="Group Name" required style={{ flex: "2 1 220px" }}>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Metagenomics Lab" />
        </Field>
        {/* Unique across groups ignoring case — only the server can know that,
            so a taken prefix comes back as its 422 sentence, shown below. */}
        <Field label={`${word({ capital: true })} number prefix`} required style={{ flex: "1 1 160px" }}>
          <Input value={prefix} onChange={e => setPrefix(e.target.value)} maxLength={16}
                 placeholder="e.g. MG" />
        </Field>
        <div style={{ display: "flex", gap: 8, marginBottom: 1 }}>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
          <Btn type="submit" disabled={busy || !name.trim() || !prefix.trim()}>
            {busy ? "Creating…" : "Create Group"}
          </Btn>
        </div>
      </form>
      {error && (
        <div role="alert" style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}
    </Card>
  );
}


// What deleting a group actually does, said before it is done rather than
// discovered afterwards. The counts are the preview's; the list of what is
// destroyed is `delete_group`'s own order of clears, in prose.
//
// These are counts, never blockers — passing them to ConfirmDialog as blockers
// would lock the button for every group that has ever had a member.
const PEOPLE_NOTE = "Staff holding a role in this group, plus the owners, members and "
  + "representatives of its projects.";

function GoingSummary({ preview }) {
  const word = useDocumentWord();
  return (
    <>
      <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151" }}>
        The group is removed for good, and everything it owns goes with it: its affiliations,
        price lists and services, its policy documents together with the signatures on them, its
        tags and custom project fields. Each destroyed document is recorded in the audit log with
        its hashes and the number of signatures it took with it.
      </p>
      <div style={{ padding: "12px 14px", borderRadius: 8, background: "#f9fafb", border: "1px solid #e5e7eb", fontSize: 13, color: "#374151" }}>
        <p style={{ margin: 0 }}>
          <strong>{preview.members} member{preview.members === 1 ? "" : "s"}</strong> and{" "}
          <strong>{preview.grants} role grant{preview.grants === 1 ? "" : "s"}</strong> in this
          group. The accounts are kept — the grants scoped here are dropped and each account's
          standing is recomputed from whatever it holds elsewhere.
        </p>
        <p style={{ margin: "8px 0 0" }}>
          {word({ plural: true, capital: true })} issued by this group and transfer jobs it ran are kept too, detached from the
          group rather than deleted. A {word()} keeps every figure it was frozen with, but loses the
          affiliation and price list it names — both are destroyed with the group, and a row still
          pointing at them would refuse to let it go. Export the {word({ plural: true })} first if that link
          matters.
        </p>
      </div>
    </>
  );
}


// ── Groups tab ────────────────────────────────────────────────────────────────

const GROUP_ACCESSORS = {
  name:    g => g.name,
  members: g => g.member_count,
  created: g => new Date(g.created_at).getTime(),
};

export default function GroupsTab({ groups, onGroupsChange }) {
  const zone = useDisplayTimezone();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  // { group, preview } while the dialog is open — the same shape and the same
  // reason as AdminUsersPage's `deleting`: the dialog states what the server
  // says it would do, so the preview has to be in hand before it opens.
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const { sorted, sortKey, sortDir, toggleSort } = useSortableColumns(groups, GROUP_ACCESSORS);
  const { widths, setWidth, totalWidth } = useColumnWidths({ name: 240, members: 140, created: 140, actions: 130 });

  // The dialog is opened from the preview, not from a sentence written here.
  // It used to be a bare window.confirm promising that "members will be
  // unassigned", which was already short of the truth about role grants and
  // became false about everything else the moment the delete started clearing
  // the group's affiliations, price lists, documents and custom fields. And a
  // group holding a project is refused — asking first is what turns that into a
  // reason the administrator can act on, instead of a 409 and a reference
  // number after the fact.
  const openDelete = async g => {
    setError(null);
    try {
      setDeleting({ group: g, preview: await api.get(`/api/admin/groups/${g.id}/deletion-preview`) });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/api/admin/groups/${deleting.group.id}`);
      setDeleting(null);
      onGroupsChange();
    } catch (err) {
      setError(err.message);
      setDeleting(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        {!creating && <Btn onClick={() => setCreating(true)}>+ New Group</Btn>}
      </div>

      {creating && (
        <GroupForm
          onSave={() => { setCreating(false); onGroupsChange(); }}
          onCancel={() => setCreating(false)}
        />
      )}

      {error && (
        <div role="alert" style={{ marginBottom: 14, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.25)", color: "#b91c1c", fontSize: 13 }}>
          {error}
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="responsive-table" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: totalWidth }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                <Th label="Group Name" sortKey="name" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.name} onWidth={w => setWidth("name", w)} />
                <Th label="People" sortKey="members" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.members} onWidth={w => setWidth("members", w)} />
                <Th label="Created" sortKey="created" activeKey={sortKey} dir={sortDir} onSort={toggleSort} width={widths.created} onWidth={w => setWidth("created", w)} />
                <Th label="" align="right" width={widths.actions} onWidth={w => setWidth("actions", w)} />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={4} style={{ padding: "32px 16px", textAlign: "center", color: "#6b7280", fontSize: 14 }}>No groups yet</td></tr>
              )}
              {sorted.map(g => (
                <tr key={g.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                  <td data-label="Group Name" style={td} title={g.name}>
                    <span style={{ fontWeight: 600, color: "#1a1a1a" }}>{g.name}</span>
                  </td>
                  <td data-label="People" style={td}>
                    {/* `rbac.group_member_ids` since 2026-09-28: staff holding a
                        role here plus the people on the group's projects, who
                        hold no grant in it. "Members" read as accounts filed in
                        the group, which a researcher no longer is. One string,
                        so the count is found whole. */}
                    <span title={PEOPLE_NOTE}
                          style={{ fontSize: 12, padding: "2px 8px", borderRadius: 10, background: G_BG, color: "#006630", fontWeight: 500 }}>
                      {`${g.member_count} ${g.member_count === 1 ? "person" : "people"}`}
                    </span>
                  </td>
                  <td data-label="Created" style={{ ...td, fontSize: 12, color: "#6b7280" }}>
                    {formatDay(g.created_at, zone)}
                  </td>
                  <td data-label="" style={{ ...td, textAlign: "right" }}>
                    <Btn variant="danger" onClick={() => openDelete(g)} style={{ padding: "5px 12px", fontSize: 12 }}>
                      Delete
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {deleting && (
        <ConfirmDialog
          title={`Delete group "${deleting.group.name}"?`}
          confirmWord={deleting.group.name}
          confirmLabel="Delete group"
          busy={deleteBusy}
          // Straight from the preview, and from the same function the delete
          // itself calls — a list computed here would be a second opinion, and
          // the two would drift.
          blockers={deleting.preview.blockers}
          onConfirm={handleDelete}
          onClose={() => setDeleting(null)}
        >
          <GoingSummary preview={deleting.preview} />
        </ConfirmDialog>
      )}
    </div>
  );
}

